import timeline from '../../assets/timeline.jpg';
import timelineVert from '../../assets/timelineVert.jpg';
import styles from './ResearchPage.module.css';

const ResearchPage = (): JSX.Element => {
  return (
    <div className={styles.container}>
      <div className={styles.title}>Research Process and Goals</div>
      <div className={styles.subsection}>
        <div className={styles.subsectionTitle}>The Problem</div>
        <div className={styles.subsectionContent}>
          Encrypting traffic has increasingly become the norm on the Internet.
          Some of this encrypted traffic deals with extremely sensitive data,
          such as banking information or medical records. As such, it is
          critical that encryption algorithms employed on the Internet remain as
          secure as possible.
          <br />
          <br />
          One unique threat to modern day encryption comes in the form of
          quantum computers. Thus, in recent years, in order to keep data
          secure, new “post-quantum” algorithms, which rely on problems that are
          thought to be difficult even for a quantum computer to solve, have
          been proposed and standardized. Many of these post-quantum
          cryptosystems are based on the problem of finding characteristics of a
          mathematical object known as a lattice.
          <br />
          <br />
          Lattice cryptosystems are the main focus of our work. In particular,
          we examine the security of lattice cryptosystems in the presence of
          extra “side information” about the secret key utilized in the
          encryption process.{' '}
          <a
            href="https://eprint.iacr.org/2022/1345"
            className={styles.previousLink}
          >
            Previous work
          </a>{' '}
          has created a mathematical framework in which various forms of side
          information can be analyzed in a unified manner. We seek to expand on
          this framework by implementing new features and applying it to
          existing attacks in order to more concretely quantify the security of
          lattice cryptosystems.
        </div>
      </div>
      <div className={styles.subsection}>
        <div className={styles.subsectionTitle}>Research Questions</div>
        <div className={styles.subsectionContent}>
          <ol type="1" className={styles.orderedList}>
            <li>
              Can we improve the integration of an inequality hint, introduced
              by Dachman-Soled et. al. for attacking a DBDD instance, by using
              maximally inscribed ellipsoids instead of minimally circumscribed
              ones?
            </li>
            <li>
              Can an alternative embedding that leverages the formation of an
              LWE instance within the approximate hints used when attacking a
              DBDD instance outperform the current embedding used in the attack?
            </li>
            <li>
              Can we use the DBDD toolkit to improve an existing attack on
              CRYSTALS-Kyber?
            </li>
            <li>
              Can we determine the level of bit security provided by
              noise-flooding amounts in CKKS and find an amount that balances
              both security and performance?
            </li>
          </ol>
        </div>
      </div>
      <div className={styles.subsection}>
        <div className={styles.subsectionTitle}>Research Approach</div>
        <div className={styles.subsectionContent}>
          <ol type="1" className={styles.orderedList}>
            <li>
              Study the{' '}
              <a
                href="https://eprint.iacr.org/2022/1345"
                className={styles.previousLink}
              >
                toolkit
              </a>{' '}
              and learn how it can be used to answer our research questions
            </li>
            <li>Design and write implementations for our research questions</li>
            <li>
              Analyze these implementations both theoretically and empirically
            </li>
          </ol>
        </div>
      </div>

      <div className={styles.divider}></div>
      <img
        src={timeline}
        alt="Timeline of Project"
        className={styles.timelineImage}
      />
      <img
        src={timelineVert}
        alt="Timeline of Project"
        className={styles.timelineImageVert}
      />
    </div>
  );
};

export default ResearchPage;
